/* eslint-disable max-len */
/* eslint-disable no-unused-vars */
/* eslint-disable no-multiple-empty-lines */
/* eslint-disable import-helpers/order-imports */

import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';

import { AppContext } from '../../../contexts/app';
import api from '../../../services/api';

const TIPO_DOCUMENTO_OPTIONS = ['NOTA FISCAL', 'CUPOM FISCAL', 'RECIBO'];

function NicashReembolsoEdit(_props, ref) {
  const { setLoading, notificationRef } = useContext(AppContext);
  const [show, setShow] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [onSaveCallback, setOnSaveCallback] = useState(null);
  const [validationError, setValidationError] = useState('');
  const [editingComprovante, setEditingComprovante] = useState(null);
  const [showFileInput, setShowFileInput] = useState(true);
  const [isChangingComprovante, setIsChangingComprovante] = useState(false);
  const [fileError, setFileError] = useState('');
  const [formState, setFormState] = useState({
    formaPagamento: 'nicash',
    responsavelTipo: 'paciente',
    clienteNome: '',
    clienteCpf: '',
    descricao: '',
    valor: '',
    dataCompra: '',
    localCompra: '',
    autorizadoPor: '',
    tipoDocumento: '',
    numeroDocumento: '',
    ultimosNumerosCartao: '',
    file: null,
  });

  const editingId = useMemo(
    () => currentRow?.profissionalReembolsoId || currentRow?._id,
    [currentRow],
  );

  const hasExistingComprovante = useMemo(
    () => !!editingComprovante?.filename,
    [editingComprovante],
  );

  const resetComprovante = useCallback(() => {
    setEditingComprovante(null);
    setShowFileInput(true);
    setIsChangingComprovante(false);
    setFileError('');
    setFormState((prev) => ({ ...prev, file: null }));
  }, []);

  const handleClose = useCallback(() => {
    setShow(false);
    resetComprovante();
  }, [resetComprovante]);

  const handleDownloadComprovante = useCallback((comprovante) => {
    const filename = comprovante?.filename;
    const downloadName = comprovante?.name || filename;
    if (!filename) return;

    (async () => {
      try {
        setLoading({
          loading: true,
          message: 'Baixando arquivo...',
        });
        const res = await api.get(`/download/${filename}/files`, { responseType: 'blob' });
        const fileURL = window.URL.createObjectURL(res.data);
        const alink = document.createElement('a');
        alink.href = fileURL;
        alink.target = '_blank';
        alink.rel = 'noopener noreferrer';
        alink.download = downloadName;
        alink.click();
        window.URL.revokeObjectURL(fileURL);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        notificationRef.current.notify({
          message: error?.response?.data?.message || 'Falha ao baixar comprovante',
          color: 'danger',
        });
      }
    })();
  }, [notificationRef, setLoading]);

  const open = useCallback((row, onSave) => {
    setCurrentRow(row);
    setOnSaveCallback(() => onSave);
    setValidationError('');
    const comprovante = row?.comprovante || null;
    const hasComprovante = !!comprovante?.filename;
    setEditingComprovante(comprovante);
    setShowFileInput(!hasComprovante);
    setIsChangingComprovante(false);
    setFileError('');
    setFormState({
      formaPagamento: row?.formaPagamento || 'nicash',
      responsavelTipo: row?.responsavelPeloGasto || 'paciente',
      clienteNome: row?.paciente?.nome || '',
      clienteCpf: row?.paciente?.cpf || '',
      descricao: row?.motivoGasto || '',
      valor: row?.valorAPagar ?? '',
      dataCompra: row?.dataCompra ? row.dataCompra.substring(0, 10) : '',
      localCompra: row?.localCompra || '',
      autorizadoPor: row?.especialista || '',
      tipoDocumento: row?.tipoDocumento || '',
      numeroDocumento: row?.numeroDocumento || '',
      ultimosNumerosCartao: row?.numeroCartao || '',
      file: null,
    });
    setShow(true);
  }, []);

  useImperativeHandle(ref, () => ({
    show: open,
    close: handleClose,
  }), [open, handleClose]);

  const onChange = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    if (field === 'file' && value instanceof File) {
      setFileError('');
    }
  }, []);

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    setValidationError('');

    if (!editingId || !currentRow?.profissionalId) {
      notificationRef.current.notify({
        message: 'Não foi possível identificar o reembolso para edição.',
        color: 'danger',
      });
      return;
    }

    if (!formState.tipoDocumento) {
      setValidationError('Informe o tipo de documento.');
      return;
    }

    const needsFile = !hasExistingComprovante || isChangingComprovante;
    if (needsFile && !(formState.file instanceof File)) {
      setFileError('Envie o comprovante');
      return;
    }
    setFileError('');

    try {
      setLoading({ loading: true, message: 'Atualizando reembolso...' });

      const formData = new FormData();
      formData.append('cliente', JSON.stringify({
        id: currentRow?.paciente?.id || '',
        cpf: formState.clienteCpf || '',
        nome: formState.clienteNome || '',
      }));
      formData.append('responsavelTipo', formState.responsavelTipo || '');
      formData.append('formaPagamento', formState.formaPagamento || '');
      formData.append('descricao', formState.descricao || '');
      formData.append('valor', String(formState.valor || ''));
      formData.append('dataCompra', formState.dataCompra || '');
      formData.append('localCompra', formState.localCompra || '');
      formData.append('autorizadoPor', formState.autorizadoPor || '');
      formData.append('tipoDocumento', formState.tipoDocumento || '');
      formData.append('numeroDocumento', formState.numeroDocumento || '');
      formData.append('ultimosNumerosCartao', formState.formaPagamento === 'reembolso' ? '' : (formState.ultimosNumerosCartao || ''));
      if (formState.file instanceof File) {
        formData.append('file', formState.file);
      }

      const { data } = await api.put(
        `/financeiroProfissionalReembolsos/${editingId}?profissionalId=${currentRow.profissionalId}`,
        formData,
      );

      setLoading(false);
      notificationRef.current.notify({ message: 'Reembolso atualizado com sucesso' });
      handleClose();
      if (typeof onSaveCallback === 'function') {
        onSaveCallback(data);
      }
    } catch (error) {
      setLoading(false);
      notificationRef.current.notify({
        message: error?.response?.data?.message || 'Falha ao atualizar reembolso',
        color: 'danger',
      });
    }
  }, [
    currentRow,
    editingId,
    formState,
    handleClose,
    hasExistingComprovante,
    isChangingComprovante,
    notificationRef,
    onSaveCallback,
    setLoading,
  ]);

  return (
    <Modal isOpen={show} centered toggle={handleClose}>
      <Form onSubmit={onSubmit}>
        <ModalHeader>
          <strong>Editar Reembolso</strong>
          <Button
            className="close position-absolute top-4 right-4 py-1 px-2"
            onClick={handleClose}
          >
            <i className="fas fa-times" />
          </Button>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Forma de Pagamento</Label>
            <Input type="select" value={formState.formaPagamento} onChange={(e) => onChange('formaPagamento', e.target.value)}>
              <option value="nicash">CARTÃO COORPORATIVO</option>
              <option value="reembolso">REEMBOLSO/CARTÃO PESSOAL</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Responsável pelo gasto</Label>
            <Input type="select" value={formState.responsavelTipo} onChange={(e) => onChange('responsavelTipo', e.target.value)}>
              <option value="nic_sp">NIC São Paulo</option>
              <option value="nic_go">NIC Goiania</option>
              <option value="paciente">Paciente/Cliente/Família</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Responsável (Nome)</Label>
            <Input type="text" value={formState.clienteNome} onChange={(e) => onChange('clienteNome', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Responsável (CPF)</Label>
            <Input type="text" value={formState.clienteCpf} onChange={(e) => onChange('clienteCpf', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Descrição do item Comprado</Label>
            <Input type="textarea" value={formState.descricao} onChange={(e) => onChange('descricao', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Valor da Compra</Label>
            <Input type="number" step="0.01" value={formState.valor} onChange={(e) => onChange('valor', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Data da Compra</Label>
            <Input type="date" value={formState.dataCompra} onChange={(e) => onChange('dataCompra', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Local da Compra</Label>
            <Input type="text" value={formState.localCompra} onChange={(e) => onChange('localCompra', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>Autorizado por</Label>
            <Input type="text" value={formState.autorizadoPor} onChange={(e) => onChange('autorizadoPor', e.target.value)} />
          </FormGroup>
          <FormGroup>
            <Label>TIPO DE DOCUMENTO</Label>
            <Input
              type="select"
              value={formState.tipoDocumento}
              invalid={!!validationError}
              onChange={(e) => onChange('tipoDocumento', e.target.value)}
            >
              <option value="">Selecione</option>
              {TIPO_DOCUMENTO_OPTIONS.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </Input>
            <FormFeedback>{validationError}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label>Número do documento</Label>
            <Input type="text" value={formState.numeroDocumento} onChange={(e) => onChange('numeroDocumento', e.target.value)} />
          </FormGroup>
          {formState.formaPagamento !== 'reembolso' && (
            <FormGroup>
              <Label>4º ÚLTIMOS NUMEROS DO CARTÃO UTILIZADO</Label>
              <Input type="text" value={formState.ultimosNumerosCartao} onChange={(e) => onChange('ultimosNumerosCartao', e.target.value)} />
            </FormGroup>
          )}
          <FormGroup>
            <Label for="comprovante-nicash-reembolso">
              {showFileInput ? 'Enviar Comprovante (*)' : 'Comprovante'}
            </Label>
            {!showFileInput && editingComprovante?.filename ? (
              <div className="d-flex">
                <Button
                  color="info"
                  size="sm"
                  className="mr-2"
                  type="button"
                  onClick={() => handleDownloadComprovante(editingComprovante)}
                >
                  <i className="fa-solid fa-download mr-1" />
                  Visualizar
                </Button>
                <Button
                  color="warning"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setShowFileInput(true);
                    setIsChangingComprovante(true);
                  }}
                >
                  Alterar comprovante
                </Button>
              </div>
            ) : (
              <Input
                id="comprovante-nicash-reembolso"
                type="file"
                accept="image/*"
                invalid={!!fileError}
                onChange={({ target: { files } }) => onChange('file', files?.[0] || null)}
              />
            )}
            <FormFeedback style={{ display: fileError ? 'block' : 'none' }}>
              {fileError}
            </FormFeedback>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="success" type="submit">Atualizar</Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default forwardRef(NicashReembolsoEdit);
